import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import logo from "../assets/brand/logo.png";
import logo1 from "../assets/images/passport.png";
import dataEn from "../dataEn";
import dataKin from "../dataKin";
import { language } from "../tools/language";
const data = language === "en" ? dataEn : dataKin;

function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [userName, setUserName] = useState("");
  const [position, setPosition] = useState("");
  const navigate = useNavigate();
  const inactivityTimerRef = useRef(null);

  // Set inactivity timeout (2 hours = 2 * 60 * 60 * 1000 milliseconds)
  const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000;

  // Function to reset the inactivity timer
  const resetInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    inactivityTimerRef.current = setTimeout(() => {
      handleAutoLogout();
    }, INACTIVITY_TIMEOUT);
  };

  // Function to handle automatic logout
  const handleAutoLogout = () => {
    // Clear authentication token and user data from local storage
    localStorage.removeItem("token");
    localStorage.removeItem("userName");
    localStorage.removeItem("position");
    localStorage.removeItem("lastActivity");
    
    // Redirect to the login page
    navigate("/login");
  };

  // Function to setup event listeners for user activity
  const setupActivityListeners = () => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      document.addEventListener(event, resetInactivityTimer, true);
    });
  };

  // Function to remove event listeners
  const cleanupActivityListeners = () => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      document.removeEventListener(event, resetInactivityTimer, true);
    });
  };

  useEffect(() => {
    // Check if the user is logged in
    const token = localStorage.getItem("token");
    if (token) {
      // Retrieve the user's name from local storage
      const storedUserName = localStorage.getItem("userName");
      const storedPosition = localStorage.getItem("position");
      if (storedUserName) {
        setUserName(storedUserName);
        setPosition(storedPosition);
      }

      // Check if there's existing last activity time
      const lastActivity = localStorage.getItem("lastActivity");
      const currentTime = new Date().getTime();

      if (lastActivity) {
        const timeSinceLastActivity = currentTime - parseInt(lastActivity);
        
        // If more than 2 hours have passed since last activity, auto logout
        if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
          handleAutoLogout();
          return;
        } else {
          // Set timer for remaining time
          const remainingTime = INACTIVITY_TIMEOUT - timeSinceLastActivity;
          inactivityTimerRef.current = setTimeout(() => {
            handleAutoLogout();
          }, remainingTime);
        }
      } else {
        // Set initial last activity time
        localStorage.setItem("lastActivity", currentTime.toString());
        resetInactivityTimer();
      }

      // Setup activity listeners
      setupActivityListeners();

      // Update last activity time every minute to handle browser tab inactivity
      const activityInterval = setInterval(() => {
        localStorage.setItem("lastActivity", new Date().getTime().toString());
      }, 60000); // Update every minute

      // Cleanup function
      return () => {
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
        cleanupActivityListeners();
        clearInterval(activityInterval);
      };
    } else {
      // Redirect to the login page if the user is not logged in
      navigate("/login");
    }
  }, [navigate]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = () => {
    // Clear timers and listeners
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    cleanupActivityListeners();
    
    // Clear authentication token and user data from local storage
    localStorage.removeItem("token");
    localStorage.removeItem("userName");
    localStorage.removeItem("position");
    localStorage.removeItem("lastActivity");
    
    // Redirect to the login page
    navigate("/login");
  };

  return (
    <div className="top-bar-boxed h-[70px] md:h-[65px] z-[51] border-b border-white/[0.08] mt-12 md:mt-0 -mx-3 sm:-mx-8 md:-mx-0 px-3 md:border-b-0 relative md:fixed md:inset-x-0 md:top-0 sm:px-8 md:px-10 md:pt-10 md:bg-gradient-to-b md:from-slate-100 md:to-transparent dark:md:from-darkmode-700">
      <div className="h-full flex items-center">
        <a href="/" className="logo -intro-x hidden md:flex xl:w-[180px] block">
          <img alt="" className="logo__image w-12" src={logo} />
          <span className="logo__text text-white text-lg ml-5 mt-3">
            {data.names.firstName}
          </span>
        </a>
        <nav aria-label="breadcrumb" className="-intro-x h-[45px] mr-auto ml-0">
          <ol className="breadcrumb breadcrumb-light">
            <li className="breadcrumb-item">
              <a href="/">{data.title}</a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {data.user}
            </li>
          </ol>
        </nav>
        <div className="intro-x relative mr-3 sm:mr-6">
          <div className="search hidden sm:block">
            <input
              type="text"
              className="search__input form-control border-transparent"
              placeholder="Search..."
            />
            <Search className="search__icon dark:text-slate-500" />
          </div>
          <a className="notification notification--light sm:hidden" href="/">
            <Search className="notification__icon dark:text-slate-500" />
          </a>
        </div>

        <div className="intro-x dropdown w-8 h-8" onClick={toggleDropdown}>
          <div
            className="dropdown-toggle w-8 h-8 rounded-full overflow-hidden shadow-lg image-fit zoom-in scale-110"
            role="button"
            aria-expanded={isOpen ? "true" : "false"}
            data-tw-toggle="dropdown"
          >
            <a href="#">
              <img alt="" src={logo1} />
            </a>
          </div>
          {isOpen && (
            <div className="absolute top-0 right-0 mt-[3.5em] bg-[#164E63] p-2 w-[200px] rounded border-color">
              <ul className=" text-white">
                <li className="p-2">
                  <div className="font-medium m-2">{userName}</div>
                </li>
                <div className="text-[#c1c1c1] font-medium p-4">{position}</div>
                {/* <li>
                  <hr className="dropdown-divider m-2 border-white/[0.08]" />
                </li>
                <li>
                  <a href="/" className="dropdown-item m-2 hover:bg-white/5"> <i data-lucide="user" className="w-4 h-4 mr-2"></i> Profile </a>
                </li>            */}
                <li>
                  <hr className="dropdown-divider m-2 border-white/[0.08]" />
                </li>
                <li>
                  <button
                    onClick={handleLogout}
                    className="dropdown- m-2 hover:bg-white/5"
                  >
                    {" "}
                    <i
                      data-lucide="toggle-right"
                      className="w-4 h-4 mr-2"
                    ></i>{" "}
                    Logout{" "}
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Header;